// src/pages/Photos/PodiumPhotoDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FiCamera,
  FiCheck,
  FiCopy,
  FiExternalLink,
  FiImage,
  FiLink,
  FiSave,
  FiX,
} from "react-icons/fi";

const FALLBACK_IMAGE = "/img/no-image.jpg";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (String(photoUrl).startsWith("http")) return photoUrl;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const clean = String(photoUrl).replaceAll("\\", "/").replace(/^\/+/, "");

  return `${base}/${clean}`;
};

export default function PodiumPhotoDrawer({
  open,
  mode = "create",
  initial,
  selectedCompetition,
  onClose,
  onSave,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    photo_url: "",
    description: "",
  });

  const photoUrl = String(form.photo_url || "").trim();

  const previewUrl = useMemo(() => {
    return toAbsolutePhotoUrl(photoUrl) || FALLBACK_IMAGE;
  }, [photoUrl]);

  useEffect(() => {
    if (!open) return;

    setForm({
      photo_url: initial?.photo_url || "",
      description: initial?.description || "",
    });
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSave({
      photo_url: form.photo_url,
      description: form.description,
    });
  };

  const copyToClipboard = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch {
      window.prompt("Copier l’URL :", value);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="relative overflow-hidden bg-slate-950 p-5 text-white sm:p-6">
          <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
          <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl">
                <FiCamera />
              </div>

              <h2 className="text-2xl font-bold tracking-tight">
                {isEdit ? "Modifier la photo" : "Ajouter une photo"}
              </h2>

              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                {selectedCompetition
                  ? selectedCompetition.nom_competition
                  : "Photo de podium"}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fermer"
            >
              <FiX />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-4 sm:p-6"
        >
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="relative h-72 overflow-hidden bg-slate-100 dark:bg-slate-800">
              <img
                src={previewUrl}
                alt={form.description || "Aperçu photo"}
                className="h-full w-full object-contain bg-white dark:bg-slate-950"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK_IMAGE;
                }}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none" />

              <div className="absolute bottom-4 left-4 right-4">
                <div className="inline-flex rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-800 backdrop-blur">
                  Aperçu de la photo
                </div>

                <h3 className="mt-3 line-clamp-2 text-2xl font-bold text-white drop-shadow">
                  {form.description || "Photo de podium"}
                </h3>
              </div>
            </div>

            <div className="border-t border-slate-200 p-4 dark:border-slate-800">
              <div className="flex flex-wrap gap-2">
                {photoUrl ? (
                  <>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-950/70 dark:text-indigo-300 dark:hover:bg-indigo-950"
                    >
                      <FiExternalLink />
                      Voir l’image
                    </a>

                    <button
                      type="button"
                      onClick={() => copyToClipboard(photoUrl)}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    >
                      <FiCopy />
                      Copier l’URL
                    </button>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    <FiImage />
                    Aucune URL renseignée
                  </span>
                )}

                {isEdit ? (
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300">
                    <FiCheck />
                    Photo existante
                  </span>
                ) : null}
              </div>
            </div>
          </section>

          <div className="mt-5 grid grid-cols-1 gap-4">
            <Field label="URL de la photo" icon={FiLink} required>
              <input
                className={INPUT}
                value={form.photo_url}
                name="photo_url"
                onChange={handleChange}
                placeholder="https://res.cloudinary.com/.../photo.jpg"
                required
              />

              <p className="mt-2 text-xs font-medium text-slate-400">
                Actuellement, la photo est enregistrée via une URL Cloudinary.
                Plus tard, vous pourrez remplacer cette logique par un upload
                direct si besoin.
              </p>
            </Field>

            <Field label="Description" icon={FiImage}>
              <input
                className={INPUT}
                value={form.description}
                name="description"
                onChange={handleChange}
                placeholder="Ex : Podium Minimes -66kg, Benjamines, etc."
              />

              <p className="mt-2 text-xs font-medium text-slate-400">
                La description permettra d’identifier plus facilement la photo
                dans l’espace public.
              </p>
            </Field>
          </div>
        </form>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FiX />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-pink-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              <FiSave />
              {isEdit ? "Enregistrer" : "Ajouter la photo"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, icon: Icon, children, required = false }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-indigo-500" />
        {label}
        {required ? <span className="text-rose-500">*</span> : null}
      </span>

      {children}
    </label>
  );
}
